// Cloudflare store: talks to the memorybox Worker API (D1 today; R2 media
// and a Durable Object for realtime in later phases). Library-free plain
// fetch, so notes paint the instant the page loads. Same interface as
// SupabaseStore — the boot code picks one or the other and never cares which.

import type { Note, NoteUpdate } from "@ts/core/types.ts";
import type { BoardEvent, MediaChange, NewNote, Store } from "@ts/store/store.ts";

export class CloudflareStore implements Store {
  readonly remote = true;
  private seen = new Set<string>(); // note ids we've already surfaced
  private poll: ReturnType<typeof setInterval> | null = null;

  constructor(
    private api: string, // Worker base URL
    private slug: string, // board slug (in the URL)
    private roomWord: string | null = null,
    private boardId: string | null = null
  ) {}

  /** Attach a room word mid-session — no reload, so a half-typed note survives. */
  setRoomWord(word: string): void {
    this.roomWord = word;
  }

  private headers(extra?: Record<string, string>): Record<string, string> {
    const h: Record<string, string> = { "content-type": "application/json", ...extra };
    if (this.roomWord) h["x-room-word"] = this.roomWord;
    return h;
  }

  async init(): Promise<Date | null> {
    // A multi-box board's freeze date is already in the config loaded at
    // boot; nothing to fetch here.
    return null;
  }

  async list(): Promise<Note[]> {
    const res = await fetch(`${this.api}/b/${this.slug}/notes`);
    if (!res.ok) throw new Error(`list failed: ${res.status}`);
    const rows: Note[] = await res.json();
    for (const n of rows) this.seen.add(n.id);
    return rows;
  }

  private async upload(blob: Blob, kind: "photo" | "audio"): Promise<string> {
    const res = await fetch(`${this.api}/b/${this.slug}/media?kind=${kind}`, {
      method: "POST",
      headers: this.headers({ "content-type": blob.type || "application/octet-stream" }),
      body: blob,
    });
    if (!res.ok) throw new Error(`media upload failed: ${res.status}`);
    const { path } = await res.json();
    return path as string;
  }

  async create(note: NewNote, media: MediaChange) {
    const body: Record<string, unknown> = { ...note };
    if (media.photo) body.photo_path = await this.upload(media.photo, "photo");
    if (media.audio) body.audio_path = await this.upload(media.audio, "audio");
    const res = await fetch(`${this.api}/b/${this.slug}/notes`, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(body),
    });
    if (res.status === 403) {
      // Reuse the exact wrong-room-word retry UX the Supabase store triggers
      // (main.ts keys off err.code === "42501").
      const b = await res.json().catch(() => ({}));
      if (/room word/i.test(b.error || "")) {
        const e = new Error("room word rejected") as Error & { code?: string };
        e.code = "42501";
        throw e;
      }
    }
    if (!res.ok) throw new Error(`create failed: ${res.status}`);
    const { note: created, token } = await res.json();
    this.seen.add(created.id);
    return { note: created as Note, token: token as string };
  }

  async update(id: string, token: string | null, fields: NoteUpdate, media: MediaChange) {
    const body: Record<string, unknown> = { ...fields };
    if (media.photo) body.photo_path = await this.upload(media.photo, "photo");
    else if (media.removePhoto) body.photo_path = "";
    if (media.audio) body.audio_path = await this.upload(media.audio, "audio");
    else if (media.removeAudio) body.audio_path = "";

    const res = await fetch(`${this.api}/notes/${id}`, {
      method: "PATCH",
      headers: this.headers(token ? { "x-note-token": token } : undefined),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`update failed: ${res.status}`);
    const updated = await res.json();

    const patch: Partial<Note> = { ...fields };
    if (fields.name !== undefined) patch.name = fields.name || null;
    if (body.photo_path !== undefined) patch.photo_url = updated.photo_url ?? null;
    if (body.audio_path !== undefined) patch.audio_url = updated.audio_url ?? null;
    return patch;
  }

  async remove(id: string, token: string | null) {
    const res = await fetch(`${this.api}/notes/${id}`, {
      method: "DELETE",
      headers: this.headers(token ? { "x-note-token": token } : undefined),
    });
    if (!res.ok && res.status !== 404) throw new Error(`delete failed: ${res.status}`);
  }

  async saveEmail(email: string) {
    await fetch(`${this.api}/b/${this.slug}/emails`, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify({ email }),
    });
  }

  track(visitor: string, name: string, props?: Record<string, unknown>) {
    fetch(`${this.api}/events`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ board_id: this.boardId, visitor, name, props: props ?? null }),
    }).then(
      () => {},
      () => {} // analytics must never break the experience
    );
  }

  // Live updates over a WebSocket to the board's Durable Object. Note
  // changes and presence arrive as pushed messages. Reconnects on drop,
  // and every (re)connect reconciles against the REST list so nothing is
  // missed while the socket was down. A dead socket degrades to a slow
  // poll so the board never goes fully stale.
  subscribe(onEvent: (e: BoardEvent) => void, onPresence?: (count: number) => void) {
    const wsUrl = this.api.replace(/^http/, "ws") + `/b/${this.slug}/live`;
    let closedByUs = false;

    // Diff the authoritative list against what we've surfaced; emit the gap.
    const reconcile = async () => {
      try {
        const res = await fetch(`${this.api}/b/${this.slug}/notes`);
        if (!res.ok) return;
        const rows: Note[] = await res.json();
        const ids = new Set(rows.map((n) => n.id));
        for (const n of rows) {
          if (!this.seen.has(n.id)) {
            this.seen.add(n.id);
            onEvent({ type: "upsert", note: n });
          }
        }
        for (const id of [...this.seen]) {
          if (!ids.has(id)) {
            this.seen.delete(id);
            onEvent({ type: "delete", note: { id } as Note });
          }
        }
      } catch {
        /* ignore — the next reconcile catches up */
      }
    };

    const connect = () => {
      let ws: WebSocket;
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        return startPolling(); // ancient browser / blocked WS
      }
      ws.onopen = () => void reconcile(); // catch anything missed while down
      ws.onmessage = (ev) => {
        let m: { type: string; count?: number; note?: Note };
        try {
          m = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (m.type === "presence") {
          onPresence?.(m.count ?? 1);
        } else if (m.type === "upsert" && m.note) {
          this.seen.add(m.note.id);
          onEvent({ type: "upsert", note: m.note });
        } else if (m.type === "delete" && m.note) {
          this.seen.delete(m.note.id);
          onEvent({ type: "delete", note: m.note });
        }
      };
      ws.onclose = () => {
        if (!closedByUs) setTimeout(connect, 3000); // gentle reconnect
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          /* already closing */
        }
      };
    };

    const startPolling = () => {
      onPresence?.(1);
      this.poll = setInterval(reconcile, 6000);
    };

    connect();
    void closedByUs; // reserved for a future explicit teardown
  }
}
